import { MermaidRenderer } from '@/catalog-apps/components/component-library/MdTextComponent/MermaidRenderer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/shadcn/alert-dialog'
import { Button } from '@/components/shadcn/button'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { getMarkdownContent } from '@/pages/Applications/AppTermsPage/useGetTerms'
import { useGetPrivacyPolicyMarkdown } from '@/pages/Applications/AppPrivacyPolicyPage/useGetPrivacyPolicy'
import type { ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

type PrivacyPolicyDialogProps = {
  children: ReactNode
  appId: number | undefined
}

export function PrivacyPolicyDialog({ appId, children }: PrivacyPolicyDialogProps) {
  const { data } = useGetPrivacyPolicyMarkdown(appId)
  const privacyPolicyContent = getMarkdownContent(data)

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="max-h-[85vh] max-w-4xl grid-rows-[auto_minmax(0,1fr)_auto]">
        <AlertDialogHeader>
          <AlertDialogTitle>Privacy Policy</AlertDialogTitle>
          <AlertDialogDescription>Please review the application privacy policy.</AlertDialogDescription>
        </AlertDialogHeader>

        <ScrollArea className="min-h-0 rounded-md border bg-background px-5 py-4">
          <div className="legal-markdown prose prose-sm max-w-none dark:prose-invert">
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                pre({ children, ...props }) {
                  const codeElement = children as React.ReactElement

                  if (codeElement?.props?.className?.includes('language-mermaid')) {
                    return <MermaidRenderer chart={String(codeElement.props.children).replace(/\n$/, '')} />
                  }

                  return <pre {...props}>{children}</pre>
                },
                code({ className, children, ...props }) {
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  )
                },
              }}
            >
              {privacyPolicyContent}
            </Markdown>
          </div>
        </ScrollArea>

        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button type="button">Close</Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
